import {
  Upload,
  downloadUpload,
  deleteUpload,
  getMyUploadsPage,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import { TableColumn } from "@/config/types";
import {
  downloadBlob,
  downloadFile,
  getFileFromBlobResponse,
} from "@/config/utils";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const LIMIT = 50;

export function useUploadsList() {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang, selectedMandateId } = useContext(PspContext);
  const isTablet = useIsTablet();
  const navigate = useNavigate();

  // -----------------------------------------------------------------------------
  // STATES
  // -----------------------------------------------------------------------------
  const [files, setFiles] = useState<Upload[]>();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingUploads, setIsLoadingUploads] = useState(true);
  const [fileActionOpenId, setFileActionOpenId] = useState<string | null>(null);
  const [deleteFileConfirmationOpenId, setDeleteFileConfirmationOpenId] =
    useState<string | null>(null);
  const [currentFilePreview, setCurrentFilePreview] = useState<File | null>(
    null,
  );
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>();

  // -----------------------------------------------------------------------------
  // FETCH INITIAL DATA
  // -----------------------------------------------------------------------------
  const actualSearchQuery = searchQuery.length >= 2 ? searchQuery : undefined;
  function fetchFilesHelper() {
    if (selectedMandateId) {
      setIsLoadingUploads(true);
      getMyUploadsPage({
        query: {
          mandate_id: selectedMandateId,
        },
        body: {
          limit: LIMIT,
          offset: LIMIT * page,
          query: actualSearchQuery,
        },
      }).then(({ data, error }) => {
        setIsLoadingUploads(false);
        if (error) {
          toast(translate("couldntLoadUploadedDocuments", lang));
          return;
        }
        setFiles(data.data);
        setIsLoadingUploads(false);
        setTotalPages(Math.ceil(data.total / LIMIT) || 1);
      });
    }
  }

  useEffect(() => {
    fetchFilesHelper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMandateId, actualSearchQuery, page]);

  // -----------------------------------------------------------------------------
  // MEMOIZED VALUES
  // -----------------------------------------------------------------------------
  const columns: TableColumn[] = [
    { key: "type", label: translate("type", lang), className: "w-10" },
    {
      key: "documentType",
      label: translate("documentType", lang),
      className: "min-w-38 w-1/6",
    },
    { key: "name", label: translate("name", lang), className: "w-3/6" },
    {
      key: "createdAt",
      label: translate("createdAt", lang),
      className: "min-w-24 w-1/6",
    },
    {
      key: "size",
      label: translate("size", lang),
      className: "min-w-24 w-1/6",
    },
    { key: "action", label: translate("action", lang), className: "w-16" },
  ];

  const isLoading = useMemo(() => isLoadingUploads, [isLoadingUploads]);

  // -----------------------------------------------------------------------------
  // CALLBACK FUNCTIONS
  // -----------------------------------------------------------------------------
  function onDownload(uploadId?: string) {
    if (uploadId) {
      downloadUpload({
        path: { upload_id: uploadId },
        responseType: "blob",
      }).then((response) => {
        if (response.error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          downloadBlob(response);
        }
      });
    } else if (currentFilePreview) {
      downloadFile(currentFilePreview);
      setCurrentFilePreview(null);
    } else {
      toast(translate("somethingWentWrong", lang));
    }
  }

  function onDelete() {
    if (deleteFileConfirmationOpenId) {
      deleteUpload({ path: { upload_id: deleteFileConfirmationOpenId } }).then(
        ({ error }) => {
          if (error) {
            toast(translate("somethingWentWrong", lang));
            return;
          }
          toast(translate("documentDeletedSuccessfully", lang));
          setDeleteFileConfirmationOpenId(null);
          fetchFilesHelper();
        },
      );
    } else {
      toast(translate("somethingWentWrong", lang));
    }
  }

  function onOpenPreview(uploadId: string) {
    downloadUpload({
      path: { upload_id: uploadId },
      responseType: "blob",
    }).then((response) => {
      if (response.error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setCurrentFilePreview(getFileFromBlobResponse(response));
      }
    });
  }

  return {
    isLoading,
    onDownload,
    onDelete,
    columns,
    files,
    uploadDialogOpen,
    setUploadDialogOpen,
    isTablet,
    navigate,
    setSearchQuery,
    selectedMandateId,
    searchQuery,
    lang,
    fileActionOpenId,
    setFileActionOpenId,
    deleteFileConfirmationOpenId,
    setDeleteFileConfirmationOpenId,
    onOpenPreview,
    fetchFilesHelper,
    currentFilePreview,
    setCurrentFilePreview,
    totalPages,
    page,
    setPage,
  };
}
